import { TerminalMsg } from '@/components/organisation/interface/IAdvanceCompiler'
import { useAdvanceCompilerStore } from '@/stores/advanceCompiler.store'
import { useIdeStore } from '@/stores/ide.store'

/**
 * Creates a WebSocket connection to the terminal service
 * @returns WebSocket instance
 */
const createWebSocketService = () => {
  let hostname = ''
  if (process.env.NODE_ENV === 'development') {
    const proxyEnv = import.meta.env.VITE_PROXY_BACKEND_URL as string
    hostname = proxyEnv?.replace(/^https?:\/\/|\/$/g, '')
  } else {
    hostname = window.location.hostname
  }

  const socket = new WebSocket(
    `wss://${hostname}/adv-engine/terminal?lang=${useIdeStore().ideMeta?.language}`
  )

  return socket
}

/**
 * Establish a WebSocket receivers
 * @param index will be the last index of the array because new terminal is being pushed to last
 * @param reConnect will be true when reconnecting same existing socket
 */
const connectReceivers = (index: number, reConnect: boolean = false) => {
  let terminalCreated = false

  const advCompilerStore = useAdvanceCompilerStore()

  const id = advCompilerStore.terminals[index].id
  const socket = advCompilerStore.terminals[index].socket

  socket.onmessage = function (event) {
    const idx = advCompilerStore.terminals.findIndex((value) => value.id === id)
    const xterm = advCompilerStore.terminals[idx].xterm

    if (event.data === TerminalMsg.CREATING) {
      terminalCreated = true
      return
    }

    if (terminalCreated && event.data === '$') {
      advCompilerStore.setReadOnlyTerminal(idx, false)
      xterm.options.cursorBlink = true
      xterm.focus()
    }

    /**
     * When reconnecting socket preventing "$"" message being displayed
     */
    if (event.data == '$' && reConnect) {
      reConnect = false
      return
    }

    /**
     * Regex checks for terminal prompt patterns:
     * 1. `$\s` - dollar sign followed by space
     * 2. `>\s` - greater-than sign followed by space
     * 3. `\x1b\[20G` - ANSI escape sequence for cursor positioning at column 20
     * It determines whether the terminal is in a prompt state, toggling read-only mode and cursor blinking accordingly.
     */
    if (event.data !== '$') {
      // eslint-disable-next-line no-control-regex
      const regex = /(\$\s|>\s|\x1b\[20G)$/
      const passed = regex.test(event.data)
      advCompilerStore.setReadOnlyTerminal(idx, !passed)
      xterm.options.cursorBlink = passed
    }

    xterm.write(event.data === '$' ? event.data + ' ' : event.data)
    advCompilerStore.appendIthTerminalHistory(
      idx,
      event.data === '$' ? event.data + ' ' : event.data
    )
  }
}

/**
 * Send a message via WebSocket
 * @param socket - WebSocket instance
 * @param message - The message to send
 */
const sendMessage = (socket: WebSocket, message: string) => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(message)
  } else if (socket && socket.readyState === WebSocket.CONNECTING) {
    socket.addEventListener('open', () => socket.send(message), { once: true })
  }
}

/**
 * Close the WebSocket connection
 * @param socket - WebSocket instance
 */
const disconnect = (socket: WebSocket) => {
  if (socket) {
    socket.close()
  }
}

export { createWebSocketService, connectReceivers, sendMessage, disconnect }
